// SPDX-License-Identifier: GPL-3.0-or-later
// The compact menu of the header. On a phone the header must fit one row, so How to play,
// Featured turns and Settings go behind one Menu button. On a wide screen the CSS shows the items
// in the row and hides the Menu button, so this module has nothing to do there.
// The menu closes after a pick, on Escape, and on a press outside of it.

/** What happens to the menu. */
export type MenuEvent = "toggle" | "pick" | "escape" | "outside";

/** The menu opens with the Menu button only. Every other event closes it. */
export function nextMenuOpen(open: boolean, event: MenuEvent): boolean {
  return event === "toggle" ? !open : false;
}

/** The nodes of the menu. A real element fits each interface. */
export interface MenuNodes {
  /** The element that holds the Menu button and the items. */
  readonly root: {
    contains(node: unknown): boolean;
    addEventListener(type: "keydown", listener: (ev: { readonly key: string }) => void): void;
  };
  readonly toggle: {
    setAttribute(name: string, value: string): void;
    focus(): void;
    addEventListener(type: "click", listener: () => void): void;
  };
  readonly list: {
    toggleAttribute(name: string, force: boolean): boolean;
    addEventListener(type: "click", listener: () => void): void;
  };
}

/** The part of the document that the menu reads. */
export interface MenuDocument {
  addEventListener(type: "pointerdown", listener: (ev: { readonly target: unknown }) => void): void;
}

/** Bind the menu of a real page. */
export function bindPageMenu(
  doc: Document,
  root: HTMLElement,
  toggle: HTMLElement,
  list: HTMLElement,
): void {
  bindMenu(doc as unknown as MenuDocument, { root, toggle, list } as unknown as MenuNodes);
}

/** Bind the menu. It starts closed. */
export function bindMenu(doc: MenuDocument, nodes: MenuNodes): void {
  let open = false;
  const apply = (event: MenuEvent): void => {
    open = nextMenuOpen(open, event);
    nodes.toggle.setAttribute("aria-expanded", String(open));
    nodes.list.toggleAttribute("data-open", open);
  };
  apply("outside");
  nodes.toggle.addEventListener("click", () => apply("toggle"));
  nodes.list.addEventListener("click", () => apply("pick"));
  nodes.root.addEventListener("keydown", (ev) => {
    if (ev.key !== "Escape" || !open) return;
    apply("escape");
    nodes.toggle.focus();
  });
  doc.addEventListener("pointerdown", (ev) => {
    if (open && !nodes.root.contains(ev.target)) apply("outside");
  });
}
